import { useEffect, useState } from "react";
import { toast } from "@/lib/toast";
import { Paperclip, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import ConfirmDeleteFileDialog from "@/components/common/ConfirmDeleteFileDialog";
import FileOrScanDialog from "@/components/common/FileOrScanDialog";
import UploadedFileThumbnail from "@/components/common/UploadedFileThumbnail";
import AttachmentImagePreview from "@/components/common/AttachmentImagePreview";
import AttachmentActions from "@/components/common/AttachmentActions";
import api from "@/lib/api";

const humanSize = (b) => (b < 1024 ? `${b} o` : b < 1e6 ? `${(b / 1024).toFixed(1)} Ko` : `${(b / 1e6).toFixed(1)} Mo`);

// Spec rev57 (2026-08-02) — jaune brand du module Tâches, cohérent avec
// UploadingOverlay + UploadedFileThumbnail dans les autres écrans Tâches.
const ACCENT_TASK = "#F9BC26";

// Fenêtre temporelle en secondes durant laquelle un fichier fraîchement
// uploadé apparaît avec le badge Check vert (« isNew »). Miroir du
// comportement rev46 côté Documents.
const NEW_WINDOW_S = 8;

export default function AttachmentsPanel({ taskId, frozen = false }) {
  const [items, setItems] = useState([]);
  const [attachOpen, setAttachOpen] = useState(false);
  // Suivi des IDs récemment uploadés (bascule le badge Check vert).
  const [freshIds, setFreshIds] = useState(new Set());
  // Modal de confirmation de suppression (spec rev57 — remplace window.confirm).
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/tasks/${taskId}/attachments`);
      setItems(data);
    } catch { /* silent */ }
  };

  useEffect(() => { load(); }, [taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploader = async (file, { onProgress, signal }) => {
    const fd = new FormData();
    fd.append("file", file);
    await api.post(`/tasks/${taskId}/attachments`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => onProgress?.((e.loaded / (e.total || file.size)) * 100, e.loaded),
      signal,
    });
  };

  // Après un batch d'uploads : recharge la liste puis marque les nouveaux
  // IDs comme « fresh » pour NEW_WINDOW_S secondes (badge Check vert visible).
  const onAllDone = async () => {
    const before = new Set(items.map((x) => x.id));
    await load();
    // Re-fetch pour repérer les nouveaux IDs
    try {
      const { data } = await api.get(`/tasks/${taskId}/attachments`);
      const nowNew = data.filter((x) => !before.has(x.id)).map((x) => x.id);
      if (nowNew.length > 0) {
        setFreshIds((prev) => new Set([...prev, ...nowNew]));
        setTimeout(() => {
          setFreshIds((prev) => {
            const next = new Set(prev);
            for (const id of nowNew) next.delete(id);
            return next;
          });
        }, NEW_WINDOW_S * 1000);
      }
    } catch { /* silent */ }
  };


  // Spec rev57 (2026-08-02) — suppression via AlertDialog (aligné sur le
  // pattern BrokenStepsAlert / UsersSection / StorageSection déjà en
  // place ailleurs). Fond `#0F0F10`, bordure `#2A2A2A`, action rouge
  // avec état busy.
  const confirmDelete = async () => {
    if (!pendingDelete || deleteBusy) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/tasks/${taskId}/attachments/${pendingDelete.id}`);
      setItems((prev) => prev.filter((x) => x.id !== pendingDelete.id));
      setPendingDelete(null);
      toast.success("Pièce jointe supprimée");
    } catch (err) {
      toast.error("Suppression refusée", { description: err.response?.data?.detail || "" });
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div className="rounded-md border border-[#3A3A3A] p-4 bg-white/[0.02] shadow-[0_2px_8px_rgba(0,0,0,0.3)]" data-testid="attachments-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
          <Paperclip size={14} /> Pièces jointes ({items.length})
        </h3>
        {!frozen && <Button
          variant="outline"
          size="sm"
          onClick={() => setAttachOpen(true)}
          data-testid="attach-add"
        >
          <Upload size={12} className="mr-1" /> Ajouter
        </Button>}
      </div>

      <FileOrScanDialog
        open={attachOpen}
        onOpenChange={setAttachOpen}
        uploader={uploader}
        onAllDone={onAllDone}
        title="Ajouter des pièces jointes"
        testIdPrefix="attach-panel-dialog"
      />

      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Aucun fichier.</p>
      ) : (
        /* Spec rev57 (2026-08-02) — refonte visuelle :
           grille compacte de miniatures 44×44 (UploadedFileThumbnail avec
           accent jaune Tâches) + nom + taille + actions. Le badge Check
           vert « isNew » apparaît pour les fichiers uploadés dans les
           NEW_WINDOW_S dernières secondes. */
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {items.map((a) => {
            const isNew = freshIds.has(a.id);
            return (
              <li
                key={a.id}
                className="flex items-center gap-2.5 py-1.5 px-2 rounded transition-colors hover:bg-white/[0.04]"
                style={{ border: "1px solid #2A2A2A", background: "#141414" }}
                data-testid={`attach-item-${a.id}`}
              >
                <AttachmentImagePreview
                  filename={a.original_filename}
                  downloadUrl={`/tasks/${taskId}/attachments/${a.id}/download`}
                  side="right"
                >
                  <UploadedFileThumbnail
                    filename={a.original_filename}
                    accent={ACCENT_TASK}
                    isNew={isNew}
                    size={44}
                    testid={`attach-thumb-${a.id}`}
                  />
                </AttachmentImagePreview>
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] text-white truncate leading-tight" title={a.original_filename}>
                    {a.original_filename}
                  </div>
                  <div className="text-[10.5px] text-muted-foreground tabular-nums mt-0.5">
                    {humanSize(a.size)}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <AttachmentActions url={`/tasks/${taskId}/attachments/${a.id}/download`} filename={a.original_filename} contentType={a.content_type} downloadColor="#5A9EE0" testId={`attach-${a.id}`} />
                  {!frozen && <button
                    type="button"
                    className="inline-flex items-center justify-center transition-colors hover:bg-white/5"
                    style={{ width: 30, height: 30, borderRadius: 6, border: "0.5px solid #3a3a40", background: "#1c1c20", color: "#EF4444" }}
                    onClick={() => setPendingDelete(a)}
                    title="Supprimer"
                    data-testid={`attach-delete-${a.id}`}
                  >
                    <Trash2 size={12} strokeWidth={2.4} />
                  </button>}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Iter209 sextans (2026-02-28) — modale unifiée
          ConfirmDeleteFileDialog partagée avec Documents. */}
      <ConfirmDeleteFileDialog
        open={!!pendingDelete}
        onOpenChange={(v) => { if (!v && !deleteBusy) setPendingDelete(null); }}
        filename={pendingDelete?.original_filename}
        onConfirm={confirmDelete}
        busy={deleteBusy}
        title="Supprimer la pièce jointe ?"
      />
    </div>
  );
}
